/**
 * Mock工具函数
 * 统一消费 featureFlags 中的 analysisMockRuntime 配置。
 */

import { analysisMockRuntime } from '@/config/featureFlags';

// 运行时覆盖值（可通过控制台调整）
let runtimeDelay = null;
let runtimeFailureRate = null;

/**
 * 设置mock延迟（可在浏览器控制台调用）
 * @param {number} ms - 延迟毫秒数，设为0无延迟
 */
export const setMockDelay = (ms) => {
    runtimeDelay = ms;
    console.log(`[Mock] Delay set to ${ms}ms`);
};

/**
 * 设置随机失败率（可在浏览器控制台调用）
 * @param {number} rate - 0-1之间的小数
 */
export const setMockFailureRate = (rate) => {
    runtimeFailureRate = Math.max(0, Math.min(1, rate));
    console.log(`[Mock] Failure rate set to ${(runtimeFailureRate * 100).toFixed(1)}%`);
};

/**
 * 获取当前mock延迟
 */
export const getMockDelay = () => {
    // 优先级：运行时覆盖 > 统一 runtime config 默认值
    if (runtimeDelay !== null) return runtimeDelay;
    return analysisMockRuntime.delayMs;
};

/**
 * 获取当前失败率
 */
export const getMockFailureRate = () => {
    if (runtimeFailureRate !== null) return runtimeFailureRate;
    return analysisMockRuntime.failureRate;
};

/**
 * 模拟随机失败（用于测试错误处理）
 * @param {string} operation - 操作描述，用于日志
 * @throws {Error} 根据失败率随机抛出错误
 */
export const simulateRandomFailure = (operation = 'mock_request') => {
    const rate = getMockFailureRate();
    if (rate > 0 && Math.random() < rate) {
        const error = new Error(`[Mock] Simulated failure for ${operation}`);
        error.code = 'mock_simulated_failure';
        error.status = 500;
        error.isMockError = true;
        throw error;
    }
};

// ==========================================
// 工具函数
// ==========================================

export const generateId = () => {
    return Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
};

export const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * 路由参数匹配（支持 :id 占位符）
 * @param {string} pattern - 路由模式，如 '/analysis/subjects/:id'
 * @param {string} actualPath - 实际路径，如 '/analysis/subjects/sub_abc123'
 * @returns {Object|null} - 匹配到的参数对象或null
 */
export const matchRoute = (pattern, actualPath) => {
    const normalizePath = (value) => {
        const raw = String(value || '').split('?')[0].split('#')[0];
        if (!raw) {
            return '/';
        }

        const normalized = raw.replace(/\/+$/, '');
        return normalized || '/';
    };

    const patternParts = normalizePath(pattern).split('/');
    const pathParts = normalizePath(actualPath).split('/');

    if (patternParts.length !== pathParts.length) return null;

    const params = {};
    for (let i = 0; i < patternParts.length; i++) {
        const patternPart = patternParts[i];
        const pathPart = pathParts[i];

        if (patternPart.startsWith(':')) {
            const paramName = patternPart.substring(1);
            params[paramName] = pathPart;
        } else if (patternPart !== pathPart) {
            return null;
        }
    }

    return params;
};
